import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useQueryString } from '#lib/hooks/use-query-string.js';
import { DndContext, DragOverlay, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { PlanDetail } from '#@planner/planner/detail.jsx';
import { OverviewCell, SortableOverviewCell, PlanRow, SortablePlanRow } from '#@planner/planner/days-overview.jsx';
import { AddRowButton } from '#@planner/planner/row.jsx';
import { cn } from '#root/lib/helpers/class.js';
import { toast } from 'react-hot-toast';
import PopUp from '#components/popup.jsx';
import { isDefined } from '#lib/utils/undefined.js';
import { nanoid } from 'nanoid';
import { SectionHeading } from '#components/headings.jsx';
import { usePlannerDragAndDrop } from './use-planner-dnd.js';
import { TemplatesModal } from '#components/templates-modal.jsx';
import { FlightModal } from '#components/flight-modal.jsx';
import { useGetTransferTypes } from './use-get-transfer-types.js';
import { UserContext } from '#src/index.jsx';
import useFlights from '#hooks/use-flights.js';

export default function Planner(props) {
    const {
        itineraryId,
        places,
        editPlaces,
        plannerError,
        filters,
        plans,
        documents,
        handleAnchor,
        days,
        addDay,
        deleteDay,
        moveDay,
        addPlan,
        addFlight,
        deletePlan,
        movePlan,
        addRoom,
        deleteRoom,
        edit,
        moveGallery,
        useTemplate,
        handleError,
        setUnsavedChanges,
        date,
        enablePlanPrice,
        currencies,
        currency,
        totalFilters,
        showItineraryPreview,
        profileLanguage
    } = props;

    // TODO: INSERIRE LOGICA DI MODIFICA PLANS E DAYS IN UN HOOK
    const [selectedDayIndex, setSelectedDayIndex] = useState(0);
    const [selectedItemId, setSelectedItemId] = useQueryString('item');
    const [selectedPlanId, setSelectedPlanId] = useState(null);

    const [selectedRoomId, setSelectedRoomId] = useState(null);
    const [showPopUp, setShowPopUp] = useState(false);
    const [showFlightModal, setShowFlightModal] = useState(false);
    const [showTemplatesModal, setShowTemplatesModal] = useState(false);
    const [multiselection, setMultiselection] = useState(false);
    const [mode, setMode] = useState(null);
    const { transferTypes } = useGetTransferTypes();
    const { updateFlight } = useFlights({
        onUpdate: (data) => {
            const { day, id, diffDays, ...rest } = data;
            const fromIndex = day - diffDays;
            edit([`day-${fromIndex}`, id], rest, '', true);
            if (diffDays !== 0) {
                const planIndex = days[fromIndex].plans.findIndex((plan) => plan.id === id);
                const daysToAdd = day - days.length + 1;
                if (daysToAdd > 0) {
                    for (let i = 0; i < daysToAdd; i++) {
                        addDay();
                    }
                }
                movePlan([fromIndex, planIndex], [day, 0]);
            }
        }
    });

    const popUpOptions = [
        'accommodation',
        'activity',
        'flight',
        'transfer',
        'custom',
        'info',
        'suggestion',
        'template'
    ];

    const selectDay = (dayIndex) => {
        setSelectedDayIndex(dayIndex);
    };

    const { getLanguage } = useContext(UserContext);
    const lang = getLanguage();

    const currentDate = date ? new Date(date) : null;
    const daysDate = currentDate;

    const total = days?.length;
    const selectedDay = days?.[selectedDayIndex];

    // si aggiunge un giorno per ogni notte in eccesso rispetto al numero di giorni
    useEffect(() => {
        const maxIndex = days.reduce((acc, day, index) => {
            const accommodations = day.plans.filter((plan) => plan.type === 'accommodation');
            acc = Math.max(acc, index, ...accommodations.map((el) => index + Number(el.nights)));
            return acc;
        }, 0);
        const diff = maxIndex - days.length + 1;
        if (diff > 0) {
            for (let i = 0; i < diff; i++) {
                addDay(total);
            }
        }
    }, [days]);

    //MANAGE DAYS
    const addNewDay = useCallback(() => {
        addDay(total);
        setSelectedDayIndex(total);
        // logic to increment end date
    }, [addDay, setSelectedDayIndex, total]);

    const removeDay = useCallback(
        (id) => {
            const confirm = window.confirm(_t.global.confirm());
            if (!confirm) return;
            deleteDay(id);
            if (selectedDayIndex === total) setSelectedDayIndex(total - 1);
        },
        [deleteDay, setSelectedDayIndex, total]
    );

    //MANAGE PLANS
    const selectPlan = useCallback(
        (id) => {
            setSelectedPlanId(id);
            handleAnchor(`#${id}`);
        },
        [handleAnchor, setSelectedPlanId]
    );

    useEffect(() => handleAnchor(`#${selectedPlanId}`), [selectedPlanId]);

    const addNewPlan = useCallback(
        (type, total, providedId, multiselection) => {
            let id = providedId;
            if (type !== 'template') {
                id = addPlan(total || selectedDayIndex, type);
                if (Number.isNaN(selectedDayIndex)) {
                    toast.error('Select a day');
                }
            }

            selectPlan(id);

            if (type === 'template') {
                setShowTemplatesModal(true);
                setMultiselection(multiselection);
                setMode('upload');
            }

            return id;
        },
        [addPlan, selectPlan, selectedDayIndex, total]
    );

    const removePlan = useCallback(
        (dayId, id) => {
            const confirm = window.confirm(_t.global.confirm());
            if (!confirm) return;
            if (selectedPlanId === id) setSelectedPlanId(null);
            deletePlan(dayId, id);
        },
        [deletePlan, selectedPlanId]
    );

    //MANAGE ROOMS
    const addNewRoom = useCallback(
        (planId) => {
            const roomId = nanoid();
            setSelectedRoomId(roomId);
            addRoom(selectedDayIndex, planId, roomId);
            if (Number.isNaN(selectedDayIndex)) {
                toast.error('Select a day');
            }
        },
        [addRoom, selectedDayIndex]
    );

    const removeRoom = useCallback(
        (dayId, planId, id) => {
            const confirm = window.confirm(_t.global.confirm());
            if (!confirm) return;
            if (selectedRoomId === id) setSelectedRoomId(null);
            deleteRoom(dayId, planId, id);
        },
        [deleteRoom, selectedRoomId]
    );

    //MANAGE CHECKOUT
    const handleCheckOut = useCallback(
        (name, nights) => {
            const confirm = window.confirm(_t.global.confirm());
            if (!confirm) return;
            const targetIndex = selectedDayIndex + Number(nights);
            if (targetIndex >= total) {
                addDay(total);
            }
            addPlan(targetIndex, 'custom', `${name} - Check Out`, '', true);
        },
        [addDay, addPlan, edit, total, selectedDayIndex, days]
    );

    const handleAddFlight = useCallback(
        (model) => {
            let id;
            if (!isDefined(selectedDayIndex)) {
                addNewDay();
                id = addFlight(days.length, model);
            } else {
                id = addFlight(selectedDayIndex, model);
            }
            setSelectedPlanId(() => id);
        },
        [addNewDay, addFlight, setSelectedPlanId, selectedDayIndex]
    );

    //MANAGE DETAIL
    let Detail;
    const detailKey = `${selectedDayIndex}-${selectedPlanId}`;

    if (selectedDay && selectedPlanId !== null) {
        const plan = plans.find((plan) => plan?.id === selectedPlanId);
        const dayId = selectedDay.id;
        const planDate = new Date(currentDate);
        if (!isNaN(planDate.getDate())) planDate.setDate(planDate.getDate() + selectedDayIndex);
        // Salva la posizione dello scroll attuale
        if (plan)
            // Impedisci lo scroll impostando la posizione dello scroll allo stato precedente
            Detail = (
                <PlanDetail
                    daysLength={total}
                    places={places}
                    editPlaces={editPlaces}
                    filters={filters}
                    key={detailKey}
                    days={days}
                    startDate={planDate}
                    total={total}
                    dayIndex={selectedDayIndex}
                    onEdit={edit}
                    onAdd={addNewRoom}
                    onDelete={removeRoom}
                    onMoveGallery={moveGallery}
                    selectedRoomId={selectedRoomId}
                    setSelectedRoomId={setSelectedRoomId}
                    dayId={dayId}
                    planId={selectedPlanId}
                    handleCheckOut={handleCheckOut}
                    itineraryDocuments={documents}
                    invalidFields={plannerError?.filter((el) => el?.plan === selectedPlanId).map((el) => el.field)}
                    handleError={handleError}
                    setUnsavedChanges={setUnsavedChanges}
                    showTemplatesModal={showTemplatesModal}
                    setShowTemplatesModal={setShowTemplatesModal}
                    enablePlanPrice={enablePlanPrice}
                    currencies={currencies}
                    currency={currency}
                    totalFilters={totalFilters}
                    transferTypes={transferTypes}
                    showItineraryPreview={showItineraryPreview}
                    handleAddFlight={handleAddFlight}
                    profileLanguage={profileLanguage}
                    updateFlight={updateFlight}
                    itineraryId={itineraryId}
                    {...plan}
                />
            );
    }

    // DRAG AND DROP
    const { dayIds, draggedPlan, draggedDay, onDragStart, onDragEnd, onDragCancel, sensors } = usePlannerDragAndDrop({
        days,
        plans,
        total,
        moveDay,
        movePlan,
        addNewDay,
        selectedDayIndex,
        setSelectedDayIndex,
        selectedItemId,
        setSelectedItemId
    });

    return (
        <section className="flex flex-col col-span-full">
            <SectionHeading id="outline" subtext={_t.edit.outlineDescription()}>
                {_t.edit.outline()}
            </SectionHeading>
            <div className={cn('w-full flex flex-col', showItineraryPreview ? '2xl:flex-row' : 'xl:flex-row')}>
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={onDragStart}
                    onDragEnd={onDragEnd}
                    onDragCancel={onDragCancel}
                >
                    <div
                        className={cn(
                            'w-full overflow-auto max-h-[1000px]',
                            showItineraryPreview ? '2xl:w-1/2' : 'xl:w-1/2'
                        )}
                        // style={{ maxHeight: '0%' }}
                    >
                        <div className="w-full pt-1">
                            <SortableContext items={dayIds} strategy={verticalListSortingStrategy}>
                                {days?.map((day, dayIndex) => {
                                    const planIds = day.plans.map((plan) => plan?.id);
                                    const dayDate = daysDate ? new Date(daysDate) : null;
                                    let title = _t.edit.planner.dayName(dayIndex + 1);
                                    // se dayDate non valorizzata vuol dire che non è arrivato il dato dal form dell'Header
                                    if (dayDate) {
                                        const dateFormatter = new Intl.DateTimeFormat(lang ?? navigator.language, {
                                            year: 'numeric',
                                            month: '2-digit',
                                            day: '2-digit'
                                        });
                                        title += ' - ' + dateFormatter.format(dayDate);
                                        daysDate.setDate(daysDate.getDate() + 1);
                                    }

                                    return (
                                        <SortableOverviewCell
                                            {...day}
                                            title={title}
                                            key={day.id}
                                            selfId={day.id}
                                            hasChild={true}
                                            dayIndex={dayIndex}
                                            selected={selectedDayIndex === dayIndex}
                                            onSelect={selectDay}
                                            onDelete={removeDay}
                                        >
                                            <ol className="w-full isolate">
                                                <SortableContext items={planIds} strategy={verticalListSortingStrategy}>
                                                    {day.plans.map((plan, planIndex) => {
                                                        let invalid = plannerError?.find(
                                                            (el) => el?.day == day.id && el?.plan == plan?.id
                                                        );
                                                        if (invalid && plan?.previewMode === 'embed')
                                                            invalid =
                                                                (plan.previewMode === 'embed' &&
                                                                    invalid.field === 'suggestion') ||
                                                                (plan.previewMode === 'modal' &&
                                                                    invalid.field === 'defaultText');

                                                        const selected = planIds[planIndex] == selectedPlanId;
                                                        return (
                                                            <SortablePlanRow
                                                                key={plan?.id}
                                                                selfId={plan?.id}
                                                                parentId={day.id}
                                                                selected={selected}
                                                                onSelect={selectPlan}
                                                                onDelete={removePlan}
                                                                invalid={invalid}
                                                                places={places}
                                                                {...plan}
                                                            />
                                                        );
                                                    })}
                                                </SortableContext>
                                                <li className="px-2 mt-3">
                                                    <AddRowButton onClick={() => setShowPopUp(true)} />
                                                </li>
                                            </ol>
                                        </SortableOverviewCell>
                                    );
                                })}
                            </SortableContext>
                            <SortableContext items={['add']} strategy={verticalListSortingStrategy}>
                                <SortableOverviewCell
                                    id="add"
                                    className="relative flex flex-col justify-between w-full space-y-2 font-semibold border-none"
                                    addDay={() => {
                                        setSelectedPlanId(null);
                                        addNewDay();
                                    }}
                                >
                                    <div className="w-full px-2 mt-3 font-medium">
                                        <AddRowButton
                                            onClick={() => {
                                                setSelectedDayIndex();
                                                setShowPopUp(true);
                                            }}
                                        />
                                    </div>
                                </SortableOverviewCell>
                            </SortableContext>
                            <PopUp
                                places={places}
                                types={popUpOptions}
                                isOpen={showPopUp}
                                onClose={(type) => {
                                    if (type === 'flight') setShowFlightModal(true);
                                    setShowPopUp(false);
                                    setMultiselection(false);
                                }}
                                addPlan={(type) => {
                                    if (!isDefined(selectedDayIndex)) {
                                        addNewDay();
                                        addNewPlan(type, total, undefined, true);
                                    } else addNewPlan(type, undefined, undefined, true);
                                }}
                            />
                            {showFlightModal && (
                                <FlightModal
                                    isOpen={showFlightModal}
                                    isCreating={true}
                                    onClose={() => setShowFlightModal(false)}
                                    startDate={date}
                                    currentDay={selectedDayIndex}
                                    addFlight={handleAddFlight}
                                    profileLanguage={profileLanguage}
                                    itineraryId={itineraryId}
                                />
                            )}
                            {showTemplatesModal && (
                                <TemplatesModal
                                    isOpen={showTemplatesModal}
                                    onClose={() => {
                                        setShowTemplatesModal(false);
                                        setMultiselection(false);
                                    }}
                                    type={plans.find((plan) => plan?.id === selectedPlanId)?.type ?? 'template'}
                                    plan={plans.find((plan) => plan?.id === selectedPlanId)}
                                    planId={selectedPlanId}
                                    editableTemplate={structuredClone(
                                        plans.find((plan) => plan?.id === selectedPlanId)
                                    )}
                                    onUseTemplate={useTemplate}
                                    mode={mode}
                                    setMode={setMode}
                                    addPlan={addNewPlan}
                                    multiselection={multiselection}
                                    lang={lang}
                                />
                            )}
                        </div>
                        <DragOverlay
                            style={{
                                cursor: 'grabbing',
                                border: 'none'
                            }}
                        >
                            {draggedDay && (
                                <OverviewCell
                                    as="div"
                                    style={{
                                        backgroundColor: '#ffffff88',
                                        border: '1px solid #00000033',
                                        borderRadius: '0.5rem',
                                        padding: '0.5rem',
                                        cursor: 'grabbing'
                                    }}
                                    index={draggedDay.index}
                                    dayIndex={draggedDay.index}
                                    overlay={true}
                                    {...draggedDay.data}
                                >
                                    <ol className="w-full isolate">
                                        {draggedDay.data.plans.map((plan) => {
                                            return (
                                                <PlanRow
                                                    key={plan?.id}
                                                    planId={plan?.id}
                                                    dayId={draggedDay.data.id}
                                                    overlay={true}
                                                    places={places}
                                                    {...plan}
                                                />
                                            );
                                        })}
                                    </ol>
                                </OverviewCell>
                            )}
                            {draggedPlan && (
                                <PlanRow
                                    as="div"
                                    style={{
                                        border: '1px solid #00000033',
                                        borderRadius: '0.5rem',
                                        padding: '0.5rem',
                                        cursor: 'grabbing'
                                    }}
                                    overlay={true}
                                    places={places}
                                    index={draggedPlan.index}
                                    {...draggedPlan.data}
                                />
                            )}
                        </DragOverlay>
                    </div>
                </DndContext>
                <div
                    className={cn(
                        'w-full bg-neutral-100',
                        Detail ? 'mt-8' : 'mt-0',
                        showItineraryPreview ? '2xl:w-1/2 2xl:bg-white 2xl:mt-0' : 'xl:w-1/2 xl:bg-white xl:mt-0'
                    )}
                >
                    {Detail ? (
                        Detail
                    ) : (
                        <section
                            className={cn('hidden relative h-full', showItineraryPreview ? '2xl:block' : 'xl:block')}
                        >
                            <div className="absolute top-0 left-0 flex items-center justify-center w-full h-full bg-neutral-50">
                                <p className="w-2/3 italic text-center text-neutral-400">
                                    {_t.edit.planner.detailText()}
                                </p>
                            </div>
                        </section>
                    )}
                </div>
            </div>
        </section>
    );
}
